(() => {
  'use strict';

  if (window.__fdCommerceLoaded) return;
  window.__fdCommerceLoaded = true;

  const OPEN_CLASS = 'is-open';
  const MOBILE_BREAKPOINT = 960;

  const afterPaint = (callback) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
  };

  const showAnimated = (element) => {
    if (!element) return;
    window.clearTimeout(element.fdHideTimer);
    element.hidden = false;
    afterPaint(() => element.classList.add(OPEN_CLASS));
  };

  const hideAnimated = (element, delay = 220) => {
    if (!element || element.hidden) return;
    element.classList.remove(OPEN_CLASS);
    window.clearTimeout(element.fdHideTimer);
    element.fdHideTimer = window.setTimeout(() => {
      element.hidden = true;
    }, delay);
  };

  class FashionDrinksHeader {
    constructor(header) {
      this.header = header;
      this.headerBar = header.querySelector('.fd-header__bar');
      this.headerSections = header.closest('.header-sections');
      this.topbar = header.querySelector('[data-fd-topbar]');
      this.topbarSlides = [...header.querySelectorAll('[data-fd-topbar-slide]')];
      this.mobileToggle = header.querySelector('[data-fd-mobile-toggle]');
      this.mobileNav = header.querySelector('[data-fd-mobile-nav]');
      this.searchToggle = header.querySelector('[data-fd-search-toggle]');
      this.searchPanel = header.querySelector('[data-fd-search-panel]');
      this.backdrop = header.querySelector('[data-fd-header-backdrop]');
      this.menuTriggers = [...header.querySelectorAll('[data-fd-menu-toggle]')];
      this.megaMenus = [...header.querySelectorAll('[data-fd-mega]')];
      this.closeTimer = 0;
      this.activeMega = null;
      this.mobileSwipe = null;
      this.mobileSwipeBlockUntil = 0;
      this.mobileSectionAlignTimer = 0;
      this.mobileSwipeHintStartTimer = 0;
      this.mobileSwipeHintTimer = 0;
      this.headerOffsetFrame = 0;
      this.topbarTimer = 0;
      this.topbarIndex = 0;
      this.isMobileViewport = window.innerWidth < MOBILE_BREAKPOINT;

      this.headerSections?.classList.add('fd-header-sections--sticky');
      this.headerSections?.classList.toggle(
        'fd-header-sections--has-topbar',
        header.classList.contains('fd-header--has-topbar')
      );

      this.bind();
      this.initTopbarRotation();
      this.syncHeaderOffset();
    }

    initTopbarRotation() {
      if (!this.topbar || this.topbarSlides.length < 2) return;

      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return;

      const pause = () => window.clearTimeout(this.topbarTimer);
      const resume = () => this.scheduleTopbarRotation();

      this.topbar.addEventListener('mouseenter', pause);
      this.topbar.addEventListener('mouseleave', resume);
      this.topbar.addEventListener('focusin', pause);
      this.topbar.addEventListener('focusout', (event) => {
        if (!this.topbar.contains(event.relatedTarget)) resume();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) pause();
        else resume();
      });

      this.scheduleTopbarRotation();
    }

    scheduleTopbarRotation() {
      window.clearTimeout(this.topbarTimer);
      if (!this.topbar || this.topbarSlides.length < 2 || document.hidden) return;

      const interval = Math.max(4000, Number(this.topbar.dataset.fdTopbarInterval || 5000));
      this.topbarTimer = window.setTimeout(() => {
        this.showNextTopbarSlide();
        this.scheduleTopbarRotation();
      }, interval);
    }

    showNextTopbarSlide() {
      const current = this.topbarSlides[this.topbarIndex];
      this.topbarIndex = (this.topbarIndex + 1) % this.topbarSlides.length;
      const next = this.topbarSlides[this.topbarIndex];
      if (!current || !next) return;

      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => current.classList.remove('is-leaving'), 500);
    }

    bind() {
      this.mobileToggle?.addEventListener('click', () => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) this.toggleDesktopMenu();
        else this.toggleMobile();
      });
      this.searchToggle?.addEventListener('click', () => this.toggleSearch());
      this.backdrop?.addEventListener('click', () => this.closeEverything());
      document.addEventListener('theme:cart-drawer:show', () => {
        this.syncHeaderOffset();
        this.closeEverything();
      }, true);

      this.header.addEventListener('click', (event) => {
        if (
          Date.now() < this.mobileSwipeBlockUntil
          && event.target.closest('[data-fd-mobile-tab-panel]')
        ) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        const mobileTab = event.target.closest('[data-fd-mobile-tab-target]');
        if (mobileTab && this.header.contains(mobileTab)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.activateMobileTab(mobileTab);
          return;
        }

      }, true);

      this.header.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'touch' || window.innerWidth < MOBILE_BREAKPOINT) return;
        const tab = event.target.closest('[data-fd-category-target]');
        if (!tab || !this.header.contains(tab)) return;
        this.activateTab(tab);
      }, true);

      this.menuTriggers.forEach((trigger) => {
        const menu = this.megaMenus.find((item) => item.dataset.fdMega === trigger.dataset.fdMenuToggle);
        if (!menu) return;

        trigger.addEventListener('click', () => {
          if (this.activeMega === menu) {
            if (this.header.classList.contains('is-desktop-menu-open')) {
              this.closeDesktopMenu();
            } else {
              this.closeMega();
            }
          } else {
            this.openMega(trigger, menu);
          }
        });

        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch' || window.innerWidth < MOBILE_BREAKPOINT) return;
          window.clearTimeout(this.closeTimer);
          this.openMega(trigger, menu);
        });

        trigger.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'touch') return;
          this.scheduleMegaClose();
        });

        menu.addEventListener('pointerenter', () => window.clearTimeout(this.closeTimer));
        menu.addEventListener('pointerleave', () => this.scheduleMegaClose());
      });

      this.header.querySelectorAll('[data-fd-category-target]').forEach((tab) => {
        const activate = () => this.activateTab(tab);
        tab.addEventListener('mouseenter', activate);
        tab.addEventListener('focus', activate);
        tab.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') activate();
        });
      });

      this.header.querySelectorAll('.fd-mobile-nav__section').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.header.querySelectorAll('.fd-mobile-nav__section[open]').forEach((item) => {
            if (item !== details) item.open = false;
          });

          window.clearTimeout(this.mobileSectionAlignTimer);
          afterPaint(() => this.alignMobileSection(details, 'smooth'));
          this.mobileSectionAlignTimer = window.setTimeout(
            () => this.alignMobileSection(details, 'auto'),
            320
          );
        });
      });

      /*
       * Listen in the capture phase so product/design links or third-party
       * theme handlers cannot swallow a horizontal category swipe.
       * The complete active panel (grid and CTA) is the gesture surface.
       */
      this.header.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        const panel = event.target.closest('[data-fd-mobile-tab-panel]');
        if (!panel || !this.header.contains(panel)) return;

        const touch = event.touches[0];
        this.mobileSwipe = {
          panel,
          x: touch.clientX,
          y: touch.clientY,
        };
      }, { passive: true, capture: true });

      this.header.addEventListener('touchcancel', () => {
        this.mobileSwipe = null;
      }, { passive: true, capture: true });

      this.header.addEventListener('touchend', (event) => {
        if (!this.mobileSwipe || event.changedTouches.length !== 1) {
          this.mobileSwipe = null;
          return;
        }

        const swipe = this.mobileSwipe;
        this.mobileSwipe = null;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - swipe.x;
        const deltaY = touch.clientY - swipe.y;

        if (Math.abs(deltaX) < 52 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

        const section = swipe.panel.closest('.fd-mobile-nav__section');
        const tabs = [...(section?.querySelectorAll('[data-fd-mobile-tab-target]') || [])];
        const activeIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
        if (activeIndex < 0) return;

        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = Math.min(tabs.length - 1, Math.max(0, activeIndex + direction));
        if (nextIndex === activeIndex) return;

        event.preventDefault();
        this.mobileSwipeBlockUntil = Date.now() + 450;
        this.activateMobileTab(tabs[nextIndex]);
      }, { passive: false, capture: true });

      this.header.querySelectorAll('[data-cart-toggle]').forEach((cartLink) => {
        cartLink.addEventListener('click', (event) => {
          this.closeEverything();

          const cartDrawer = document.querySelector('cart-drawer');
          const cartType = window.theme?.settings?.cartType;
          if (!cartDrawer || (cartType && cartType !== 'drawer')) return;

          event.preventDefault();
          cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
          if (window.a11y) window.a11y.lastElement = cartLink;
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeEverything();
      });

      window.addEventListener('resize', () => {
        this.syncHeaderOffset();
        const isMobileViewport = window.innerWidth < MOBILE_BREAKPOINT;
        if (isMobileViewport !== this.isMobileViewport) {
          this.closeDesktopMenu();
          this.closeMobile();
          this.closeMega();
          this.isMobileViewport = isMobileViewport;
        }
      });
      window.addEventListener('scroll', () => this.syncHeaderOffset(), { passive: true });
    }

    syncHeaderOffset() {
      window.cancelAnimationFrame(this.headerOffsetFrame);
      this.headerOffsetFrame = window.requestAnimationFrame(() => {
        const bottom = this.headerBar?.getBoundingClientRect().bottom;
        if (!Number.isFinite(bottom)) return;
        document.documentElement.style.setProperty(
          '--fd-header-viewport-bottom',
          `${Math.max(0, Math.round(bottom))}px`
        );
      });
    }

    activateTab(tab) {
      const target = tab.dataset.fdCategoryTarget;
      const scope = tab.closest('.fd-mega');
      const panel = [...(scope?.querySelectorAll('[data-fd-tab-panel]') || [])]
        .find((item) => item.dataset.fdTabPanel === target);
      if (!panel) return;

      scope?.querySelectorAll('[data-fd-category-target]').forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      scope?.querySelectorAll('[data-fd-tab-panel]').forEach((item) => {
        const active = item === panel;
        item.classList.toggle('is-active', active);
        item.hidden = !active;
      });
    }

    activateMobileTab(tab) {
      const target = tab.dataset.fdMobileTabTarget;
      const section = tab.closest('.fd-mobile-nav__section');
      if (!section) return;

      const tabs = [...section.querySelectorAll('[data-fd-mobile-tab-target]')];
      const panels = [...section.querySelectorAll('[data-fd-mobile-tab-panel]')];
      const panel = panels.find((item) => item.dataset.fdMobileTabPanel === target);
      if (!panel) return;

      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((item) => {
        const active = item === panel;
        item.classList.toggle('is-active', active);
        item.hidden = !active;
      });

      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    alignMobileSection(details, behavior = 'smooth') {
      const scrollArea = details?.closest('.fd-mobile-nav__body');
      if (!scrollArea || !details.open) return;

      const scrollAreaRect = scrollArea.getBoundingClientRect();
      const detailsRect = details.getBoundingClientRect();
      const targetTop = scrollArea.scrollTop + detailsRect.top - scrollAreaRect.top;

      scrollArea.scrollTo({
        top: Math.max(0, targetTop),
        behavior,
      });
    }

    showMobileSwipeHint() {
      window.clearTimeout(this.mobileSwipeHintStartTimer);
      window.clearTimeout(this.mobileSwipeHintTimer);

      this.mobileSwipeHintStartTimer = window.setTimeout(() => {
        const activePanel = this.mobileNav?.querySelector(
          '.fd-mobile-nav__section[open] [data-fd-mobile-tab-panel].is-active:not([hidden])'
        );
        if (!activePanel) return;

        activePanel.classList.remove('is-swipe-hint');
        void activePanel.offsetWidth;
        activePanel.classList.add('is-swipe-hint');
        this.mobileSwipeHintTimer = window.setTimeout(
          () => activePanel.classList.remove('is-swipe-hint'),
          760
        );
      }, 320);
    }

    openMega(trigger, menu) {
      this.closeCartDrawer();
      this.closeSearch();
      if (window.innerWidth < MOBILE_BREAKPOINT) this.closeMobile();

      if (this.activeMega && this.activeMega !== menu) {
        this.activeMega.hidden = true;
        this.activeMega.classList.remove(OPEN_CLASS);
      }

      this.menuTriggers.forEach((item) => {
        const active = item === trigger;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-expanded', String(active));
      });

      this.activeMega = menu;
      showAnimated(menu);
      showAnimated(this.backdrop);
    }

    scheduleMegaClose() {
      if (this.header.classList.contains('is-desktop-menu-open')) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closeMega(), 170);
    }

    closeMega() {
      window.clearTimeout(this.closeTimer);
      this.menuTriggers.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-expanded', 'false');
      });
      this.megaMenus.forEach((menu) => hideAnimated(menu));
      this.activeMega = null;
      if (!this.mobileNav?.classList.contains(OPEN_CLASS) && !this.searchPanel?.classList.contains(OPEN_CLASS)) {
        hideAnimated(this.backdrop, 180);
      }
    }

    toggleSearch() {
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        if (!this.mobileNav?.classList.contains(OPEN_CLASS)) {
          this.openMobile();
        }

        this.searchToggle?.setAttribute('aria-expanded', 'true');
        window.setTimeout(() => {
          const mobileSearch = this.mobileNav?.querySelector('[data-fd-mobile-search-input]');
          mobileSearch?.focus();
          mobileSearch?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 260);
        return;
      }

      const shouldOpen = !this.searchPanel?.classList.contains(OPEN_CLASS);
      this.closeCartDrawer();
      this.closeDesktopMenu();
      this.closeMobile();

      if (shouldOpen) {
        this.searchToggle?.setAttribute('aria-expanded', 'true');
        showAnimated(this.searchPanel);
        showAnimated(this.backdrop);
        window.setTimeout(() => this.searchPanel?.querySelector('[data-fd-search-input]')?.focus(), 220);
      } else {
        this.closeSearch();
      }
    }

    closeSearch() {
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      hideAnimated(this.searchPanel);
      if (!this.activeMega && !this.mobileNav?.classList.contains(OPEN_CLASS)) {
        hideAnimated(this.backdrop, 180);
      }
    }

    toggleMobile() {
      if (this.mobileNav?.classList.contains(OPEN_CLASS)) this.closeMobile();
      else this.openMobile();
    }

    toggleDesktopMenu() {
      if (this.header.classList.contains('is-desktop-menu-open')) {
        this.closeDesktopMenu();
      } else {
        this.openDesktopMenu();
      }
    }

    openDesktopMenu() {
      const firstTrigger = this.menuTriggers[0];
      const firstMenu = firstTrigger
        ? this.megaMenus.find((item) => item.dataset.fdMega === firstTrigger.dataset.fdMenuToggle)
        : null;

      this.closeCartDrawer();
      this.closeSearch();
      this.closeMobile();
      this.header.classList.add('is-desktop-menu-open');
      this.mobileToggle?.setAttribute('aria-expanded', 'true');
      this.mobileToggle?.setAttribute('aria-label', 'Menü schließen');

      if (firstTrigger && firstMenu) {
        this.openMega(firstTrigger, firstMenu);
      } else {
        showAnimated(this.backdrop);
      }
    }

    closeDesktopMenu() {
      if (!this.header.classList.contains('is-desktop-menu-open')) return;
      this.header.classList.remove('is-desktop-menu-open');
      this.mobileToggle?.setAttribute('aria-expanded', 'false');
      this.mobileToggle?.setAttribute('aria-label', 'Menü öffnen');
      this.closeMega();
    }

    openMobile() {
      this.closeCartDrawer();
      this.closeMega();
      this.closeSearch();
      this.mobileToggle?.setAttribute('aria-expanded', 'true');
      this.mobileToggle?.setAttribute('aria-label', 'Menü schließen');
      showAnimated(this.mobileNav);
      showAnimated(this.backdrop);
      document.body.classList.add('fd-mobile-nav-open');
      if (window.innerWidth < MOBILE_BREAKPOINT) this.showMobileSwipeHint();
    }

    closeMobile() {
      window.clearTimeout(this.mobileSwipeHintStartTimer);
      window.clearTimeout(this.mobileSwipeHintTimer);
      this.mobileNav?.querySelectorAll('.is-swipe-hint').forEach((panel) => {
        panel.classList.remove('is-swipe-hint');
      });
      this.mobileToggle?.setAttribute('aria-expanded', 'false');
      this.mobileToggle?.setAttribute('aria-label', 'Menü öffnen');
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      hideAnimated(this.mobileNav);
      document.body.classList.remove('fd-mobile-nav-open');
      if (!this.activeMega && !this.searchPanel?.classList.contains(OPEN_CLASS)) {
        hideAnimated(this.backdrop, 180);
      }
    }

    closeCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:hide'));
      cartDrawer.querySelector('[data-cart-drawer-close]')?.click();
    }

    closeEverything() {
      this.closeDesktopMenu();
      this.closeMega();
      this.closeSearch();
      this.closeMobile();
    }
  }

  const initHeaders = (scope = document) => {
    scope.querySelectorAll('[data-fd-header]').forEach((header) => {
      if (header.dataset.fdInitialized === 'true') return;
      header.dataset.fdInitialized = 'true';
      new FashionDrinksHeader(header);
    });
  };

  const suppressLegacyCartUpsells = (scope = document) => {
    const holders = [];

    if (scope instanceof Element && scope.matches('.product-upsell__holder--cart')) {
      holders.push(scope);
    }
    if (scope.querySelectorAll) {
      holders.push(...scope.querySelectorAll('.product-upsell__holder--cart'));
    }

    holders.forEach((holder) => {
      if (holder.closest('.fd-cart-upsell')) return;

      const legacyWidget = holder.closest('details, .cart__widget, .cart-block') || holder;
      legacyWidget.dataset.fdLegacyCartUpsell = 'true';
      legacyWidget.hidden = true;
      legacyWidget.setAttribute('aria-hidden', 'true');
    });
  };

  let legacyUpsellFrame = 0;
  const observeLegacyCartUpsells = () => {
    if (!document.body || document.body.dataset.fdLegacyUpsellObserver === 'true') return;
    document.body.dataset.fdLegacyUpsellObserver = 'true';

    const observer = new MutationObserver((mutations) => {
      const containsCartMutation = mutations.some((mutation) => (
        [...mutation.addedNodes].some((node) => (
          node instanceof Element
          && (node.matches('cart-drawer, .product-upsell__holder--cart')
            || node.querySelector?.('cart-drawer, .product-upsell__holder--cart'))
        ))
      ));
      if (!containsCartMutation) return;

      window.cancelAnimationFrame(legacyUpsellFrame);
      legacyUpsellFrame = window.requestAnimationFrame(() => suppressLegacyCartUpsells());
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const cartPath = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  };

  const formatCartMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency
    }).format(Math.max(0, Number(cents) || 0) / 100);
  };

  const qualifyingSaleTotals = (cart, node) => {
    const giftVariantId = Number(node?.dataset.giftVariantId || 0);
    const giftSku = node?.dataset.giftSku || '';

    return (cart.items || []).reduce((totals, item) => {
      const isGift = item.variant_id === giftVariantId || (giftSku && item.sku === giftSku);
      if (isGift) return totals;

      totals.original += Number(item.original_line_price ?? item.line_price ?? 0);
      totals.final += Number(item.final_line_price ?? item.line_price ?? 0);
      return totals;
    }, { original: 0, final: 0 });
  };

  const updateSaleWidgets = (cart) => {
    if (!cart?.items) return;

    const progressNodes = [...document.querySelectorAll('[data-fd-sale-progress][data-enabled="true"]')];
    progressNodes.forEach((node) => {
      const discountThreshold = Number(node.dataset.discountThreshold || 0);
      const discountPercent = Number(node.dataset.discountPercent || 0);
      const giftThreshold = Number(node.dataset.giftThreshold || 0);
      if (!discountThreshold || !giftThreshold) return;

      const totals = qualifyingSaleTotals(cart, node);
      const discountActive = totals.original >= discountThreshold;
      const giftEligible = totals.final >= giftThreshold;
      const discountRemaining = Math.max(0, discountThreshold - totals.original);
      const giftRemaining = Math.max(0, giftThreshold - totals.final);

      let progress = 0;
      if (totals.original < discountThreshold) {
        progress = (totals.original * 50) / discountThreshold;
      } else if (totals.final < giftThreshold) {
        const postDiscountStart = discountThreshold * (1 - (discountPercent / 100));
        const stageRange = Math.max(1, giftThreshold - postDiscountStart);
        progress = 50 + ((Math.max(0, totals.final - postDiscountStart) * 50) / stageRange);
      } else {
        progress = 100;
      }
      progress = Math.max(0, Math.min(100, progress));

      const message = node.querySelector('[data-fd-sale-message]');
      if (message) {
        if (!discountActive) {
          message.innerHTML = `Nur noch <strong>${formatCartMoney(discountRemaining)}</strong> bis zu <strong>${discountPercent}% Rabatt!</strong>`;
        } else if (!giftEligible) {
          message.innerHTML = `<strong>${discountPercent}% Rabatt aktiv</strong><span aria-hidden="true"> · </span>Noch <strong>${formatCartMoney(giftRemaining)}</strong> nach Rabatt bis zu den Gratis-Socken`;
        } else {
          message.innerHTML = `<strong>${discountPercent}% Rabatt aktiv</strong><span aria-hidden="true"> · </span>Gratis-Überraschungssocken freigeschaltet!`;
        }
      }

      const track = node.querySelector('[data-fd-sale-track]');
      const fill = node.querySelector('[data-fd-sale-fill]');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)));
      if (fill) fill.style.width = `${progress}%`;
      node.querySelector('[data-fd-sale-discount-marker]')?.classList.toggle('is-reached', discountActive);
      node.querySelector('[data-fd-sale-gift-marker]')?.classList.toggle('is-reached', giftEligible);
      node.dataset.giftEligible = String(giftEligible);
    });

    const referenceNode = progressNodes[0];
    document.querySelectorAll('[data-fd-sale-summary]').forEach((summary) => {
      const totals = qualifyingSaleTotals(cart, referenceNode || summary);
      const threshold = Number(summary.dataset.discountThreshold || 0);
      const savings = Math.max(0, totals.original - totals.final);
      const amount = summary.querySelector('[data-fd-sale-summary-amount]');
      const copy = summary.querySelector('[data-fd-sale-summary-copy]');

      summary.classList.toggle('has-savings', savings > 0);
      if (amount) {
        amount.textContent = savings > 0
          ? `−${formatCartMoney(savings)}`
          : `ab ${formatCartMoney(threshold)}`;
      }
      if (copy) {
        copy.innerHTML = savings > 0
          ? `Du sparst mit dem Summer Sale <strong>${formatCartMoney(savings)}</strong>`
          : `Der Rabatt wird ab ${formatCartMoney(threshold)} Warenwert automatisch berücksichtigt.`;
      }
    });

    document.querySelectorAll('[data-fd-shipping-note]').forEach((note) => {
      const threshold = Number(note.dataset.freeShippingThreshold || 0);
      const status = note.querySelector('[data-fd-shipping-status]');
      if (!threshold || !status) return;

      const cartTotal = Number(cart.total_price || 0);
      const remaining = Math.max(0, threshold - cartTotal);
      status.textContent = remaining > 0
        ? `Noch ${formatCartMoney(remaining)} bis versandkostenfrei`
        : 'Versandkostenfrei';
    });
  };

  let saleUiTimer = 0;
  let saleUiRefreshing = false;

  const refreshSaleUi = async () => {
    if (saleUiRefreshing) return;
    if (!document.querySelector('[data-fd-sale-progress], [data-fd-sale-summary], [data-fd-shipping-note]')) return;

    saleUiRefreshing = true;
    try {
      const response = await fetch(cartPath('cart.js'), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) return;
      updateSaleWidgets(await response.json());
    } catch (error) {
      console.warn('[Fashion Drinks]', error);
    } finally {
      saleUiRefreshing = false;
    }
  };

  const scheduleSaleUiRefresh = (delay = 220) => {
    window.clearTimeout(saleUiTimer);
    saleUiTimer = window.setTimeout(refreshSaleUi, delay);
  };

  let giftSyncing = false;
  let giftSyncTimer = 0;

  const getGiftConfig = () => {
    const nodes = [...document.querySelectorAll('[data-fd-sale-progress][data-enabled="true"]')];
    const node = nodes[0];
    if (!node) return null;

    const variantId = Number(node.dataset.giftVariantId);
    const threshold = Number(node.dataset.giftThreshold);
    const sku = node.dataset.giftSku || '';

    if (!variantId || !threshold) return null;
    const sectionIds = [...new Set(nodes.map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    return { variantId, threshold, sku, sectionIds };
  };

  const getQualifyingTotal = (cart, config) => cart.items.reduce((total, item) => {
    const isGift = item.variant_id === config.variantId || (config.sku && item.sku === config.sku);
    return isGift ? total : total + item.final_line_price;
  }, 0);

  const announceCartMutation = (cart, sections = null) => {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
      bubbles: true,
      detail: { cart, sections, source: 'fd-free-gift' }
    }));
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart, sections, source: 'fd-free-gift' }
    }));
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart, sections, source: 'fd-free-gift' }
    }));
  };

  const mutateGift = async (payload, endpoint, config) => {
    if (config.sectionIds.length) {
      payload.sections = config.sectionIds;
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(cartPath(endpoint), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Gratisartikel konnte nicht aktualisiert werden (${response.status}).`);
    }

    return response.json();
  };

  const syncFreeGift = async () => {
    if (giftSyncing) return;
    const config = getGiftConfig();
    if (!config) return;

    giftSyncing = true;
    try {
      const cartResponse = await fetch(cartPath('cart.js'), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Warenkorb konnte nicht geladen werden.');

      const cart = await cartResponse.json();
      updateSaleWidgets(cart);
      const giftItem = cart.items.find((item) => (
        item.variant_id === config.variantId || (config.sku && item.sku === config.sku)
      ));
      const eligible = getQualifyingTotal(cart, config) >= config.threshold;
      let changed = false;
      let renderedSections = null;

      if (eligible && !giftItem) {
        const mutation = await mutateGift({
          items: [{
            id: config.variantId,
            quantity: 1,
            properties: { '_Fashion Drinks Gratisartikel': 'Summer Sale' }
          }]
        }, 'cart/add.js', config);
        renderedSections = mutation.sections || null;
        changed = true;
      } else if (!eligible && giftItem) {
        const mutation = await mutateGift({ id: giftItem.key, quantity: 0 }, 'cart/change.js', config);
        renderedSections = mutation.sections || null;
        changed = true;
      } else if (eligible && giftItem.quantity !== 1) {
        const mutation = await mutateGift({ id: giftItem.key, quantity: 1 }, 'cart/change.js', config);
        renderedSections = mutation.sections || null;
        changed = true;
      }

      if (changed) {
        const refreshed = await fetch(cartPath('cart.js'), {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        }).then((response) => response.json());
        updateSaleWidgets(refreshed);
        announceCartMutation(refreshed, renderedSections);
      }
    } catch (error) {
      console.warn('[Fashion Drinks]', error);
    } finally {
      giftSyncing = false;
    }
  };

  const scheduleGiftSync = (delay = 280) => {
    window.clearTimeout(giftSyncTimer);
    giftSyncTimer = window.setTimeout(syncFreeGift, delay);
  };

  const initDesignCategoryPages = (scope = document) => {
    scope.querySelectorAll('[data-fd-design-page]').forEach((page) => {
      if (page.dataset.fdDesignPageReady === 'true') return;
      page.dataset.fdDesignPageReady = 'true';

      const track = page.querySelector('.fd-design-page__category-track');
      const activeCategory = page.querySelector('[data-fd-active-category]');
      if (!track || !activeCategory) return;

      afterPaint(() => {
        const left = activeCategory.offsetLeft - ((track.clientWidth - activeCategory.offsetWidth) / 2);
        track.scrollTo({
          left: Math.max(0, left),
          behavior: 'auto'
        });
      });
    });
  };

  const init = () => {
    initHeaders();
    initDesignCategoryPages();
    suppressLegacyCartUpsells();
    observeLegacyCartUpsells();
    scheduleGiftSync(80);
    scheduleSaleUiRefresh(100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initHeaders(event.target);
    initDesignCategoryPages(event.target);
    suppressLegacyCartUpsells(event.target);
    scheduleGiftSync(120);
    scheduleSaleUiRefresh(140);
  });

  ['cart:updated', 'cart:refresh', 'theme:cart:change', 'theme:cart:updated'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      suppressLegacyCartUpsells();
      if (event.detail?.cart?.items) updateSaleWidgets(event.detail.cart);
      scheduleSaleUiRefresh(240);
      if (event.detail?.source === 'fd-free-gift') {
        scheduleGiftSync(650);
      } else {
        scheduleGiftSync(350);
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-remove], [data-cart-update], cart-items quantity-counter, .cart__item')) {
      scheduleGiftSync(650);
      scheduleSaleUiRefresh(700);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-cart-form], cart-items, cart-drawer')) {
      scheduleGiftSync(650);
      scheduleSaleUiRefresh(700);
    }
  });

  document.addEventListener('theme:cart-drawer:show', () => {
    suppressLegacyCartUpsells();
    scheduleSaleUiRefresh(80);
  }, true);
})();
